import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Save, Globe, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { toast } from 'sonner';
import {
  useTranslation,
  getDefaultUiLanguageForTenant,
  UI_LANGUAGE_CHANGED_EVENT,
} from '@/components/utils/translations';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];
const ALLOWED_LANGUAGE_CODES = new Set(LANGUAGES.map((lang) => lang.code));

export default function SettingsLanguage() {
  const contextUser = useUser();
  const currentTenant = useCurrentTenant();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  useEffect(() => {
    loadLanguagePreference();
  }, [contextUser, currentTenant]);

  const loadLanguagePreference = async () => {
    if (!contextUser) return;
    setIsLoading(true);
    try {
      const adminActive =
        contextUser.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
      const fallbackTenant = resolveEffectiveTenant(adminActive, currentTenant, contextUser, 'instructure');
      const tenantDefault = getDefaultUiLanguageForTenant(fallbackTenant);
      const storedLanguage =
        contextUser.ui_language || localStorage.getItem('ui-language') || tenantDefault;
      setSelectedLanguage(ALLOWED_LANGUAGE_CODES.has(storedLanguage) ? storedLanguage : tenantDefault);
    } catch (error) {
      console.error('Error loading language preference:', error);
      const adminActive =
        contextUser.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
      const fallbackTenant = resolveEffectiveTenant(adminActive, currentTenant, contextUser, 'instructure');
      const tenantDefault = getDefaultUiLanguageForTenant(fallbackTenant);
      const storedLanguage = localStorage.getItem('ui-language') || tenantDefault;
      setSelectedLanguage(ALLOWED_LANGUAGE_CODES.has(storedLanguage) ? storedLanguage : tenantDefault);
    }
    setIsLoading(false);
  };

  const handleSaveLanguage = async () => {
    setIsSaving(true);
    try {
      const adminActive =
        contextUser.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
      const fallbackTenant = resolveEffectiveTenant(adminActive, currentTenant, contextUser, 'instructure');
      const tenantDefault = getDefaultUiLanguageForTenant(fallbackTenant);
      const safeLanguage = ALLOWED_LANGUAGE_CODES.has(selectedLanguage) ? selectedLanguage : tenantDefault;
      // Save to localStorage
      localStorage.setItem('ui-language', safeLanguage);
      window.dispatchEvent(new Event(UI_LANGUAGE_CHANGED_EVENT));

      // Save to user profile (best-effort; localStorage drives UI if this fails)
      try {
        await User.updateMyUserData({
          ui_language: safeLanguage
        });
      } catch (error) {
        console.log('Could not save to user profile, using localStorage only');
      }

      toast.success(t('languagePreferenceSaved'));
      
      // Reload so Layout/session and all pages pick up the new locale
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Error saving language preference:', error);
      toast.error(t('failedToSaveLanguagePreference'));
      setIsSaving(false);
    }
  };

  if (!contextUser) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('selectPreferredLanguageDescription')}
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t('interfaceLanguage')}
          </CardTitle>
          <CardDescription>
            {t('choosePreferredLanguage')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="language">{t('language')}</Label>
            <Select
              value={selectedLanguage}
              onValueChange={setSelectedLanguage}
            >
              <SelectTrigger id="language">
                <SelectValue placeholder={t('selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-2">
              {t('languageScopeDisclaimer')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Button 
        onClick={handleSaveLanguage} 
        disabled={isSaving}
        className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('saving')}
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            {t('saveLanguagePreference')}
          </>
        )}
      </Button>
    </div>
  );
}